import { useMemo } from 'react';

import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingSuggestionGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { EstateOnboardingSuggestionOption } from '@/modules/estate/estateAIOnboarding/EstateOnboardingSuggestionsButton';
import { ROUTE_KEYS } from '@/navigation/constants';

import { useManageIntegrationDataContext } from '../../../context/manageIntegrationData.context';

export interface TrustsBusinessesSuggestions {
  totalCount: number;
  options: EstateOnboardingSuggestionOption[];
}

/**
 * The unacknowledged AI suggestions that relate to trusts and businesses, as
 * the count badge on the tab and the menu behind the empty state's
 * "N suggestions" button.
 *
 * Reviewing a suggestion happens on the Estate page, not in this wizard, so
 * choosing one closes the modal and hands the user off there. Anything they
 * had entered but not committed is discarded — the same as closing the modal
 * any other way.
 */
export function useTrustsBusinessesSuggestions(): TrustsBusinessesSuggestions {
  const { countsByGroup } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();
  const { householdId, closeModal } = useManageIntegrationDataContext();
  const { navigate } = useNavigateToRoute();

  return useMemo(() => {
    const trustCount =
      (countsByGroup[EstateOnboardingSuggestionGroup.TrustEntities] || 0) +
      (countsByGroup[EstateOnboardingSuggestionGroup.TrustHoldings] || 0);
    const businessCount =
      (countsByGroup[EstateOnboardingSuggestionGroup.BusinessEntities] || 0) +
      (countsByGroup[EstateOnboardingSuggestionGroup.BusinessHoldings] || 0);

    const options: EstateOnboardingSuggestionOption[] = [];
    if (trustCount > 0) {
      options.push({
        key: 'trusts',
        label: 'Trusts',
        count: trustCount,
        onClick: () => {
          closeModal();
          navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, { householdId });
        },
      });
    }
    if (businessCount > 0) {
      options.push({
        key: 'businesses',
        label: 'Businesses',
        count: businessCount,
        onClick: () => {
          closeModal();
          navigate(ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES, { householdId });
        },
      });
    }

    return { totalCount: trustCount + businessCount, options };
  }, [closeModal, countsByGroup, householdId, navigate]);
}
